<template>
  <div>
    <div>
      <v-btn v-if="!isSidebar" small text @click="cancel"
        ><v-icon small color="primary">mdi-keyboard-backspace</v-icon> Back
      </v-btn>
      <v-card v-if="Boolean(providerId)" class="grey lighten-4" flat>
        <v-list-item>
          <v-list-item-avatar>
            <v-icon x-large> mdi-account-circle </v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{
              form.firstName + " " + form.lastName
            }}</v-list-item-title>
            <v-list-item-subtitle
              >Email:
              {{
                form.workEmailAddress ?? form.emailAddress
              }}</v-list-item-subtitle
            >
          </v-list-item-content>
        </v-list-item>
      </v-card>
    </div>
    <validation-observer ref="form" v-slot="{ invalid }">
      <v-card flat class="mt-2">
        <v-row v-if="isAdmin && Boolean(providerId)">
          <v-spacer></v-spacer>
          <v-switch
            v-model="editMode"
            label="Edit Mode"
            dense
            class="mx-4"
          ></v-switch>
        </v-row>
        <v-expansion-panels v-model="panels" multiple>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6"
                ><v-icon left>mdi-account-outline</v-icon> Profile</span
              >
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" sm="1" v-if="form.legacyProviderId">
                  <v-text-field
                    v-model="form.legacyProviderId"
                    label="Provider ID"
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.firstName"
                      label="First Name"
                      :readonly="!editMode"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" sm="1">
                  <validation-provider
                    :rules="{ maxLength: 1 }"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="form.middleInitial"
                      label="MI"
                      :readonly="!editMode"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" sm="3">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.lastName"
                      label="Last Name"
                      :readonly="!editMode"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-select
                    v-model="form.gender"
                    :items="genders"
                    label="Gender"
                    :readonly="!editMode"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="form.title"
                    :items="titles"
                    label="Title"
                    :readonly="!editMode"
                  ></v-select>
                </v-col> </v-row
              ><v-row>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.certification"
                    label="Certification"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.license"
                    label="License"
                    :readonly="!editMode"
                  ></v-text-field> </v-col
                ><v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.npi"
                    label="NPI"
                    :readonly="!editMode"
                  ></v-text-field> </v-col
                ><v-col cols="12" sm="3">
                  <v-select
                    v-model="form.languages"
                    :items="languages"
                    item-text="name"
                    return-object
                    label="Languages"
                    multiple
                    chips
                    dense
                    class="mt-2"
                    disabled 
                    :readonly="!editMode"
                  ></v-select>
                </v-col> </v-row
              ><v-row
                ><v-col cols="12" sm="3">
                  <v-select
                    v-model="form.disciplines"
                    :items="disciplines"
                    item-text="name"
                    return-object
                    label="Disciplines"
                    multiple
                    chips
                    dense
                    class="mt-2"
                    :readonly="!editMode"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-checkbox
                    v-model="subCodes"
                    v-for="(item, index) in disciplineSubCodes"
                    :key="index"
                    :label="item.code"
                    :value="item"
                    @change="toggleCheckbox(item)"
                    :readonly="!editMode"
                    dense
                  ></v-checkbox
                ></v-col>
                <v-col cols="12" sm="3"
                  ><v-autocomplete
                    v-model="form.supervisorId"
                    label="Supervisor"
                    :items="providers"
                    :item-text="(item) => item.firstName + ' ' + item.lastName"
                    item-value="providerId"
                    :readonly="!editMode"
                  ></v-autocomplete></v-col
                ><v-col cols="12" sm="2">
                  <v-checkbox
                    v-model="form.isBlocked"
                    label="Blocked"
                    :readonly="!editMode"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="2">
                  <!-- sets the inverted state -->
                  <v-checkbox
                    :input-value="!form.isActive"
                    @change="form.isActive = !$event"
                    label="Inactive"
                    :readonly="!editMode"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6"
                ><v-icon left>mdi-map-marker-outline</v-icon> Address</span
              >
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="form.streetAddress"
                    label="Street Address"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.streetAddressLine2"
                    label="Street Address Line 2"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="form.city"
                    label="City"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-select
                    :items="usStates"
                    v-model="form.state"
                    label="State"
                    :readonly="!editMode"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="1">
                  <v-text-field
                    v-model="form.zipCode"
                    label="ZipCode"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span class="text-h6"
                ><v-icon left>mdi-phone-outline</v-icon> Contact</span
              >
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" sm="6">
                  <validation-provider rules="email" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.emailAddress"
                      label="Email Address"
                      :readonly="!editMode"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" sm="6">
                  <validation-provider rules="email" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.workEmailAddress"
                      label="Work Email Address"
                      :readonly="!editMode"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.homePhone"
                    label="Home Phone"
                    v-mask="maskPhone"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.mobilePhone"
                    label="Mobile Phone"
                    v-mask="maskPhone"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="form.workPhone"
                    label="Work Phone"
                    v-mask="maskPhone"
                    :readonly="!editMode"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="form.preferredPhone"
                    :items="phoneTypes"
                    label="Preferred Phone"
                    :readonly="!editMode"
                  ></v-select>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card-actions v-if="isAdmin">
          <v-spacer></v-spacer>
          <v-btn @click="cancel" text>Cancel</v-btn>
          <v-btn
            @click="save"
            :disabled="loading || invalid || !editMode"
            color="primary"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </validation-observer>
  </div>
</template>

<script>
import Provider from "../../objs/Provider";
import { clearPhoneNumberFormat } from "../../utils/format-utils";

export default {
  props: ["providerId", "isSidebar"],
  data() {
    return {
      form: new Provider(),
      editMode: !Boolean(this.providerId),
      maskPhone: "(###) ###-####",
      panels: [0], // expand Profile panel
      subCodes: [],
      loading: false,
    };
  },
  async mounted() {
    if (this.providerId) {
      this.form = {
        ...this.$store.getters["optionsModule/getProviderById"](
          this.providerId
        ),
      };
      this.subCodes = this.form.disciplineSubCodes;
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters["securityModule/isAdmin"];
    },
    genders() {
      return this.$store.state.optionsModule.genders;
    },
    usStates() {
      return this.$store.state.optionsModule.usStates;
    },
    phoneTypes() {
      return this.$store.state.optionsModule.phoneTypes;
    },
    languages() {
      return this.$store.state.optionsModule.languages;
    },
    disciplines() {
      return this.$store.state.optionsModule.disciplines;
    },
    titles() {
      return this.$store.state.optionsModule.titles;
    },
    providers() {
      return this.$store.state.optionsModule.providers ?? [];
    },
    disciplineSubCodes() {
      var disciplineSubCodes =
        this.$store.state.optionsModule.disciplineSubCodes ?? [];
      return disciplineSubCodes.filter((item) =>
        this.form.disciplines?.some(
          (innerItem) => innerItem.disciplineId == item.disciplineId
        )
      );
    },
  },
  methods: {
    toggleCheckbox(item) {
      this.subCodes = this.subCodes.filter(
        (x) =>
          !this.disciplineSubCodes.some(
            (obj) => obj.disciplineSubCodeId === x.disciplineSubCodeId
          ) || x.disciplineSubCodeId == item.disciplineSubCodeId
      );
    },
    cancel() {
      this.$router.push({ name: "ProviderLookup" });
    },
    async save() {
      this.loading = true;
      this.$store.commit("uxModule/setShowLoader", true);
      //format form fields
      this.form.homePhone = clearPhoneNumberFormat(this.form.homePhone);
      this.form.mobilePhone = clearPhoneNumberFormat(this.form.mobilePhone);
      this.form.workPhone = clearPhoneNumberFormat(this.form.workPhone);
      this.form.disciplineSubCodes = this.subCodes;
      let response = { providerId: null, message: "" };

      // update
      if (this.form.providerId) {
        response = await this.$store.dispatch(
          "optionsModule/updateProvider",
          this.form
        );
      }
      // create
      else {
        response = await this.$store.dispatch(
          "optionsModule/createProvider",
          this.form
        );
        this.form = {
          ...this.$store.getters["optionsModule/getProviderById"](
            response.providerId
          ),
        };
      }
      if (response.message) {
        this.$store.commit("uxModule/setSnackbarMsg", response.message);
      } else {
        this.$store.commit(
          "uxModule/setSnackbarMsg",
          "Provider saved successfully"
        );
      }
      this.editMode = false;
      this.loading = false;
      this.$store.commit("uxModule/setShowLoader", false);
      this.$store.commit("uxModule/setShowSnackbar", true);
    },
  },
};
</script>
